import CompositeError from '../../common/utils/CompositeError';
import EventEmitter from '../../common/EventEmitter';
import { DiagnosticCode } from '../../api/diagnostics/constants';
import { DiagnosticEvent, InternalEvents } from '../../events';

export const BASE_PEER_CONFIG_ERROR_MSG = 'Peer Config request failed';

export const pushIf = (
  arr: string[],
  v?: string | number,
  prefix?: string
): void => {
  if (!isDef(v)) {
    return;
  }
  const val = prefix ? `${prefix} ${v}` : `${v}`;
  arr.push(val);
};

export const isDef = (a?: unknown) =>
  typeof a !== 'undefined' && a !== null && a !== '';

const addComma = (arr: string[], len: number, m?: unknown) => {
  if (arr.length > len && typeof m !== 'undefined') {
    arr.push(`${arr.pop()},`);
  }
};

export function constructMessage(
  status?: number,
  message?: string,
  postfix?: string
): string {
  const arr: string[] = [BASE_PEER_CONFIG_ERROR_MSG];
  pushIf(arr, status, 'with status code');
  pushIf(arr, message);
  addComma(arr, 2, postfix);
  pushIf(arr, postfix, 'statusText:');
  return arr.join(' ');
}

export interface CEArgs {
  start: number;
  url: string;
  status?: number;
  message?: string;
  diagText?: string;
}

export class ConnectionError extends Error {
  status: number;
  diagMessage: string;
  url: string;

  constructor({ diagText, message, start, status, url }: CEArgs) {
    super(constructMessage(status, message, diagText));
    this.name = 'ConnectionError';
    this.url = url;
    const interval = (ConnectionError.now() - start).toFixed(2);
    this.diagMessage = `status: ${status}; time spent: ${interval}ms`;
    if (diagText) {
      this.diagMessage = `${this.diagMessage}; statusText: ${diagText}`;
    }
    this.status = status ?? 0;
  }

  static now() {
    return performance.now();
  }
}

export function compositeErrorToDataBlock(
  ce?: CompositeError
): Record<string, string> {
  const data: Record<string, string> = {};
  if (!ce) {
    return data;
  }
  ce.errors.forEach((e: Error, idx: number) => {
    const message = e instanceof ConnectionError ? e.diagMessage : e.message;
    data[`message.${idx}`] = message;
    if (e instanceof ConnectionError) {
      // doesn't matter if we set it multiple times, it shouldn't change
      data.url = e.url;
    }
  });
  return data;
}

export default function sendPeerConfigDiagnostic(ce: CompositeError): void {
  const data = compositeErrorToDataBlock(ce);
  const event: DiagnosticEvent = {
    type: InternalEvents.DIAGNOSTIC,
    data: {
      error: {},
      data,
      from: 'getPeerConfig',
      code: DiagnosticCode.FAILED_SIGNAL_PEER_CONFIG
    }
  };
  EventEmitter.getInstance().dispatch(event);
}
